import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import type { TenantCurrentResponse } from "@/services/authService";
import { caeKeys } from "@/lib/queryKeys";
import { getMissingRequiredOnboardingCaeCfeTypes } from "@/pages/onboarding/onboardingConstants";
import type { OwnerPostCertViewSteps } from "@/pages/onboarding/ownerPostCertSteps";
import { listCaes } from "@/services/invoiceService";

interface UseOnboardingCaeGateParams {
  tenant: TenantCurrentResponse | null;
  viewStep: number;
  ownerPostCertViewSteps: OwnerPostCertViewSteps;
  isAccountantFlowOwner: boolean;
  isAccountantUser: boolean;
}

/**
 * Owns the CAE-availability gate on the owner self-onboarding CAE step: fetches
 * the tenant's CAE list, derives which required CFE types are still missing, and
 * exposes the "refreshing" flag. The step machine gates Next on
 * `hasRequiredOnboardingCaes` / `caeListFetching` and routes on `caeListLoading`.
 */
export function useOnboardingCaeGate({
  tenant,
  viewStep,
  ownerPostCertViewSteps,
  isAccountantFlowOwner,
  isAccountantUser,
}: UseOnboardingCaeGateParams) {
  const onCaeOnboardingStep =
    !isAccountantFlowOwner &&
    !isAccountantUser &&
    viewStep === ownerPostCertViewSteps.cae;
  const shouldFetchCaeListForOwnerOnboarding =
    !isAccountantFlowOwner &&
    !isAccountantUser &&
    Boolean(tenant?.hasDgiCredentials) &&
    tenant?.onboardingMode === "self" &&
    !tenant?.onboardingCompletedAt;
  const {
    data: caeList,
    isLoading: caeListLoading,
    isFetching: caeListFetching,
  } = useQuery({
    queryKey: caeKeys.list(),
    queryFn: listCaes,
    enabled: shouldFetchCaeListForOwnerOnboarding,
    staleTime: 0,
  });
  const onboardingCaeListRefreshing =
    onCaeOnboardingStep && caeListFetching && !caeListLoading;
  const missingOnboardingCaeCfeTypes = useMemo(() => {
    if (!shouldFetchCaeListForOwnerOnboarding) return [];
    return getMissingRequiredOnboardingCaeCfeTypes(caeList);
  }, [shouldFetchCaeListForOwnerOnboarding, caeList]);
  const hasRequiredOnboardingCaes = missingOnboardingCaeCfeTypes.length === 0;

  return {
    caeListLoading,
    caeListFetching,
    onboardingCaeListRefreshing,
    missingOnboardingCaeCfeTypes,
    hasRequiredOnboardingCaes,
  };
}
